import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { fmtEur } from "../lib/utils";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { FileThumb, isImageUrl } from "../components/FileThumb";
import {
  Sparkle, UploadSimple, Wallet, CreditCard, ArrowsClockwise, Palette,
  ClockCounterClockwise, WarningCircle, Envelope,
} from "@phosphor-icons/react";

const QUANTITIES = [100, 200, 500, 1000];
const LOW_STOCK_THRESHOLD_PCT = 20;

function estimatedDeliveryDate(paidAtIso, lang, businessDays = 7) {
  if (!paidAtIso) return null;
  const d = new Date(paidAtIso);
  let added = 0;
  while (added < businessDays) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  const locale = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" }[lang] || "it-IT";
  return d.toLocaleDateString(locale, { weekday: "short", day: "2-digit", month: "short" });
}

export default function ClientBranding() {
  const { t, lang } = useI18n();
  const LABELS = {
    thank_you_card: t("brand_tab_tyc"), sticker: t("brand_tab_sticker"),
    busta_tshirt: t("brand_envelope_variant_tshirt"), busta_felpa: t("brand_envelope_variant_felpa"),
  };
  const ENVELOPE_TYPES = ["busta_tshirt", "busta_felpa"];
  const STATUS_META = {
    pending_payment: { cls: "tf-status-neutral", label: t("brand_status_pending") },
    in_production: { cls: "tf-status-warning", label: t("brand_status_production") },
    available: { cls: "tf-status-success", label: t("brand_status_available") },
    exhausted: { cls: "tf-status-error", label: t("brand_status_exhausted") },
  };
  const [pricing, setPricing] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [orders, setOrders] = useState([]);
  const [productType, setProductType] = useState("thank_you_card");
  const [quantity, setQuantity] = useState(100);
  const [dimensions, setDimensions] = useState("");
  const [frontUrl, setFrontUrl] = useState("");
  const [backUrl, setBackUrl] = useState("");
  const [graphicUrl, setGraphicUrl] = useState("");
  const [uploading, setUploading] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get("/client/branding/pricing").then(({ data }) => setPricing(data));
    api.get("/client/wallet").then(({ data }) => setWallet(data));
    api.get("/client/branding/orders").then(({ data }) => setOrders(data));
  };
  useEffect(() => { load(); }, []);

  const price = pricing ? Number((pricing[productType] || {})[String(quantity)] || 0) : 0;

  const latestByType = useMemo(() => {
    const map = {};
    for (const o of orders) {
      if (!map[o.product_type] || new Date(o.created_at) > new Date(map[o.product_type].created_at)) {
        map[o.product_type] = o;
      }
    }
    return Object.values(map);
  }, [orders]);

  const uploadFile = async (file, setter, slot) => {
    if (!file) return;
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/client/branding/upload", fd);
      setter(data.url);
      toast.success(t("brand_file_uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("brand_upload_error"));
    } finally {
      setUploading("");
    }
  };

  const canSubmit = productType === "sticker"
    ? (graphicUrl && dimensions.trim())
    : ENVELOPE_TYPES.includes(productType)
      ? !!graphicUrl
      : (frontUrl && backUrl);

  const submitOrder = async (paymentMethod) => {
    if (!canSubmit) { toast.error(t("brand_error_fields")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/client/branding/orders", {
        product_type: productType, quantity, payment_method: paymentMethod,
        front_url: frontUrl || null, back_url: backUrl || null,
        graphic_url: graphicUrl || null, dimensions: dimensions || null,
        origin_url: window.location.origin,
      });
      if (paymentMethod === "stripe" && data.url) {
        window.location.href = data.url;
        return;
      }
      toast.success(t("brand_order_paid_toast", { product: LABELS[productType] }));
      setFrontUrl(""); setBackUrl(""); setGraphicUrl(""); setDimensions("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("brand_order_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const reorder = async (orderId, paymentMethod) => {
    try {
      const { data } = await api.post(`/client/branding/orders/${orderId}/reorder`, {
        payment_method: paymentMethod, origin_url: window.location.origin,
      });
      if (paymentMethod === "stripe" && data.url) {
        window.location.href = data.url;
        return;
      }
      toast.success(t("brand_reorder_paid_toast"));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("brand_reorder_error"));
    }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="client-branding">
      <PageHeader
        eyebrow={t("eyebrow_services")}
        title={t("brand_page_title")}
        subtitle={t("brand_page_subtitle")}
        chip="tf-chip-peach"
        icon={Sparkle}
      />

      {/* Active add-ons overview (most recent lot per product type) */}
      {latestByType.length > 0 && (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="branding-orders-list">
          {latestByType.map((o) => {
            const pct = o.stock_total ? Math.round((o.stock_remaining / o.stock_total) * 100) : 0;
            const isLowStock = o.status === "available" && pct <= LOW_STOCK_THRESHOLD_PCT;
            const meta = isLowStock ? { cls: "tf-status-warning", label: t("brand_low_stock", { pct }) } : (STATUS_META[o.status] || STATUS_META.pending_payment);
            return (
              <div key={o.id} className="tf-card p-5 flex flex-col min-h-[184px]" data-testid={`branding-order-${o.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-display font-bold text-base">{LABELS[o.product_type]}</div>
                    <div className="text-xs text-[var(--tf-muted)] mt-0.5">{o.quantity} {t("brand_pcs_short")} · {fmtEur(o.price)}</div>
                    {o.status === "in_production" && (
                      <div className="text-[11px] text-[var(--tf-muted)] mt-1">
                        {t("brand_delivery_estimate", { product: LABELS[o.product_type], date: estimatedDeliveryDate(o.paid_at, lang) || "—" })}
                      </div>
                    )}
                  </div>
                  <span className={`tf-status-pill ${meta.cls} shrink-0`} data-testid={`branding-status-${o.id}`}>{meta.label}</span>
                </div>
                <div className="flex-1" />
                {o.status === "available" && (
                  <div className="mt-4">
                    <div className="flex items-end justify-between mb-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("brand_remaining")}</span>
                      <span className="font-display font-black text-lg tf-numeric">{o.stock_remaining} / {o.stock_total}</span>
                    </div>
                    <div className="h-2 rounded-full bg-[var(--tf-bg)] overflow-hidden">
                      <div className={`h-full rounded-full ${isLowStock ? "bg-[#D4A017]" : "bg-[var(--tf-success)]"}`} style={{ width: `${pct}%` }} />
                    </div>
                    {isLowStock && (
                      <div className="flex items-center justify-between gap-2 mt-2.5">
                        <span className="flex items-center gap-1.5 text-[11px] text-[#D4A017] font-semibold">
                          <WarningCircle size={13} weight="fill" /> {t("brand_about_to_run_out")}
                        </span>
                        <button
                          onClick={() => reorder(o.id, "wallet")}
                          className="tf-btn tf-btn-ghost !py-1 !px-2 text-[11px]"
                          data-testid={`reorder-early-${o.id}`}
                        >
                          <ArrowsClockwise size={12} weight="bold" /> {t("brand_reorder_now")}
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {o.status === "in_production" && (
                  <div className="mt-3 text-[11px] text-[var(--tf-muted)] bg-[var(--tf-bg)] rounded-xl p-2.5">
                    {t("brand_producing_msg", { date: estimatedDeliveryDate(o.paid_at, lang) || "—" })}
                  </div>
                )}
                {o.status === "exhausted" && (
                  <>
                    <div className="mt-4 text-xs font-semibold text-[var(--tf-error)]">0 / {o.stock_total} {t("brand_remaining").toLowerCase()}</div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => reorder(o.id, "wallet")}
                        className="tf-btn tf-btn-primary flex-1 !py-2 text-xs"
                        data-testid={`reorder-wallet-${o.id}`}
                      >
                        <Wallet size={14} weight="bold" /> {t("brand_reorder_wallet")}
                      </button>
                      <button
                        onClick={() => reorder(o.id, "stripe")}
                        className="tf-btn tf-btn-ghost flex-1 !py-2 text-xs"
                        data-testid={`reorder-stripe-${o.id}`}
                      >
                        <CreditCard size={14} weight="bold" /> {t("brand_reorder_card")}
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New order form */}
      <div className="tf-card overflow-hidden">
        <div className="flex gap-1 px-5 pt-4 border-b border-[var(--tf-border)] bg-[var(--tf-bg)]/40">
          <TypeTab active={productType === "thank_you_card"} onClick={() => setProductType("thank_you_card")} label={t("brand_tab_tyc")} testid="tab-tyc" />
          <TypeTab active={productType === "sticker"} onClick={() => setProductType("sticker")} label={t("brand_tab_sticker")} testid="tab-sticker" />
          <TypeTab active={ENVELOPE_TYPES.includes(productType)} onClick={() => setProductType("busta_tshirt")} label={t("brand_tab_envelope")} testid="tab-envelope" />
        </div>

        <div className="p-5 space-y-5">
          {productType === "sticker" ? (
            <div className="space-y-4">
              <UploadBox
                label={t("brand_sticker_graphic")}
                value={graphicUrl}
                uploading={uploading === "graphic"}
                onChange={(f) => uploadFile(f, setGraphicUrl, "graphic")}
                testid="upload-graphic"
                t={t}
              />
              <div>
                <div className="tf-label mb-1.5">{t("brand_sticker_dimensions")}</div>
                <input
                  value={dimensions}
                  onChange={(e) => setDimensions(e.target.value)}
                  placeholder={t("brand_sticker_dim_placeholder")}
                  className="tf-input max-w-xs"
                  data-testid="sticker-dimensions"
                />
              </div>
            </div>
          ) : ENVELOPE_TYPES.includes(productType) ? (
            <div className="space-y-4">
              <div>
                <div className="tf-label mb-2">{t("brand_envelope_variant")}</div>
                <p className="text-xs text-[var(--tf-muted)] mb-3 max-w-lg">{t("brand_envelope_desc")}</p>
                <div className="flex gap-2 flex-wrap">
                  {ENVELOPE_TYPES.map((v) => (
                    <button
                      key={v}
                      onClick={() => setProductType(v)}
                      className={`tf-btn !py-2 text-xs ${productType === v ? "tf-btn-primary" : "tf-btn-ghost"}`}
                      data-testid={`envelope-variant-${v}`}
                    >
                      {LABELS[v]}
                    </button>
                  ))}
                </div>
              </div>
              <UploadBox
                label={t("brand_envelope_graphic")}
                value={graphicUrl}
                uploading={uploading === "graphic"}
                onChange={(f) => uploadFile(f, setGraphicUrl, "graphic")}
                testid="upload-graphic"
                t={t}
              />
            </div>
          ) : (
            <div>
              <div className="tf-label mb-2">{t("brand_format_fixed")}</div>
              <p className="text-xs text-[var(--tf-muted)] mb-3 max-w-lg">
                {t("brand_format_desc")}
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                <UploadBox
                  label={t("brand_front")}
                  value={frontUrl}
                  uploading={uploading === "front"}
                  onChange={(f) => uploadFile(f, setFrontUrl, "front")}
                  testid="upload-front"
                  t={t}
                />
                <UploadBox
                  label={t("brand_back")}
                  value={backUrl}
                  uploading={uploading === "back"}
                  onChange={(f) => uploadFile(f, setBackUrl, "back")}
                  testid="upload-back"
                  t={t}
                />
              </div>
            </div>
          )}

          <div>
            <div className="tf-label mb-2">{t("brand_quantity")}</div>
            <select
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value, 10))}
              className="tf-input max-w-xs"
              data-testid="branding-quantity-select"
            >
              {QUANTITIES.map((q) => <option key={q} value={q}>{q} {t("brand_pcs_short")}</option>)}
            </select>
          </div>

          <div className="tf-card p-4 bg-[#F5F2E8] flex items-center justify-between">
            <span className="text-xs uppercase tracking-widest text-[var(--tf-muted)] font-semibold">{t("brand_total_order")}</span>
            <span className="font-display font-black text-2xl tf-numeric" data-testid="branding-price">{pricing ? fmtEur(price) : "…"}</span>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => submitOrder("wallet")}
              disabled={!canSubmit || submitting || (wallet && wallet.balance < price)}
              className="tf-btn tf-btn-primary flex-1 py-3"
              data-testid="pay-wallet-btn"
            >
              <Wallet size={16} weight="bold" /> {t("brand_pay_wallet")} {wallet ? t("brand_pay_wallet_balance", { balance: fmtEur(wallet.balance) }) : ""}
            </button>
            <button
              onClick={() => submitOrder("stripe")}
              disabled={!canSubmit || submitting}
              className="tf-btn tf-btn-ghost flex-1 py-3"
              data-testid="pay-stripe-btn"
            >
              <CreditCard size={16} weight="bold" /> {t("brand_pay_stripe")}
            </button>
          </div>
          {wallet && wallet.balance < price && (
            <div className="text-xs text-[var(--tf-error)]">{t("brand_insufficient_balance")}</div>
          )}
        </div>
      </div>

      <div className="tf-card p-4 bg-[var(--tf-bg)]/40 flex items-center gap-3 text-xs text-[var(--tf-muted)]">
        <Palette size={20} weight="duotone" className="text-[var(--tf-ink)] shrink-0" />
        <div>
          {t("brand_note", { fee: fmtEur(pricing?.service_fee ?? 0.5) })}
        </div>
      </div>

      {/* Full order history */}
      {orders.length > 0 && (
        <div className="tf-card overflow-hidden" data-testid="branding-history">
          <div className="px-5 py-4 border-b border-[var(--tf-border)] flex items-center gap-2">
            <ClockCounterClockwise size={16} weight="bold" />
            <span className="font-display font-bold text-sm">{t("brand_history_title")}</span>
            <span className="text-xs text-[var(--tf-muted)]">({orders.length})</span>
          </div>
          <div className="divide-y divide-[var(--tf-border)]">
            {orders.map((o) => {
              const meta = STATUS_META[o.status] || STATUS_META.pending_payment;
              const files = [o.front_url, o.back_url, o.graphic_url].filter(Boolean);
              const locale = { it: "it-IT", en: "en-GB", fr: "fr-FR", de: "de-DE", es: "es-ES" }[lang] || "it-IT";
              return (
                <div key={o.id} className="flex items-center gap-3 px-5 py-3 flex-wrap" data-testid={`history-row-${o.id}`}>
                  <div className="flex gap-1.5 shrink-0">
                    {files.map((f, i) => (
                      <FileThumb key={i} url={f} size={28} />
                    ))}
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <div className="text-sm font-medium">{LABELS[o.product_type]} · {o.quantity} {t("brand_pcs_short")}</div>
                    <div className="text-[11px] text-[var(--tf-muted)]">
                      {new Date(o.created_at).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" })}
                      {" · "}{o.payment_method === "wallet" ? t("wallet") : "Stripe"}
                    </div>
                  </div>
                  <div className="text-sm font-display font-bold tf-numeric">{fmtEur(o.price)}</div>
                  <span className={`tf-status-pill ${meta.cls}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function TypeTab({ active, onClick, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2.5 font-display font-bold text-sm border-b-2 transition-colors -mb-px ${
        active
          ? "border-[var(--tf-primary)] text-[var(--tf-ink)] bg-white rounded-t-lg"
          : "border-transparent text-[var(--tf-muted)] hover:text-[var(--tf-ink)]"
      }`}
    >
      {label}
    </button>
  );
}

function UploadBox({ label, value, uploading, onChange, testid, t }) {
  return (
    <div>
      <div className="tf-label mb-1.5">{label}</div>
      <label
        className={`tf-card border-dashed p-4 flex items-center gap-3 cursor-pointer hover:border-[var(--tf-primary)] transition-colors ${value ? "border-[var(--tf-success)]" : ""}`}
        data-testid={testid}
      >
        <input
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0])}
        />
        {value ? (
          <FileThumb url={value} size={40} testid={`${testid}-thumb`} />
        ) : (
          <UploadSimple size={22} weight="bold" className="text-[var(--tf-muted)] shrink-0" />
        )}
        <div className="text-xs">
          {uploading ? t("brand_upload_uploading") : value ? t("brand_upload_replace") : t("brand_upload_placeholder")}
        </div>
      </label>
    </div>
  );
}
